/**
 * COMPONENTE DE RUTA PROTEGIDA
 *
 * Este componente protege rutas que requieren autenticación.
 * Redirige automáticamente a login si el usuario no está autenticado.
 * Ahora incluye validación de roles y redirección inteligente.
 *
 * @author Diego Alejandro
 * @version 3.0
 */

import { useAuth } from '../../context/authContext';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import PropTypes from 'prop-types';

// Configuración centralizada de roles
import { getDashboardRoute, shouldRedirectToDashboard, canAccessPath } from '../../config/roleRoutes';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirigir a login si no está autenticado, guardando la ruta actual
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/authentication/sign-in" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Si se especifican roles permitidos, verificar que el usuario tenga uno de ellos
  if (allowedRoles.length > 0 && user?.role) {
    if (!allowedRoles.includes(user.role)) {
      // Redirigir al dashboard correspondiente a su rol
      const correctRoute = getDashboardRoute(user.role);
      return <Navigate to={correctRoute} replace />;
    }
  }

  // Verificar si el usuario está tratando de acceder a una ruta incorrecta para su rol
  if (user?.role) {
    const redirectRoute = shouldRedirectToDashboard(user.role, location.pathname);
    if (redirectRoute) {
      return <Navigate to={redirectRoute} replace />;
    }
  }

  // Si está autenticado y tiene permisos, renderizar el contenido
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
};

export default ProtectedRoute;